import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Fingerprint, ScanFace } from 'lucide-react';
import { useAppStore } from '../../contexts/store';
import { refreshToken as refreshTokenRequest, getCurrentUser } from '../../api/authApi';
import {
  disableBiometric,
  getBiometryLabel,
  isBiometricEnabled,
  verifyBiometric,
  type BiometryLabel,
} from '@pp/shared/auth/biometricAuth';
import { isNativeApp } from '@pp/shared/platform/platform';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import { toast } from 'react-hot-toast';

/**
 * Launch gate shown on cold start when biometric is enabled. Verifies the
 * user with Face ID / Touch ID, then exchanges the HttpOnly refresh-token
 * cookie for a fresh access token via /api/auth/refresh.
 *
 * Failure paths:
 *   - User cancels biometric → "Try again" + "Use password" buttons
 *   - Biometric Keychain entry was invalidated (user changed biometric
 *     enrollment on the device) → fall through to /login with the flag
 *     cleared
 *   - /auth/refresh returns 401 (cookie expired) → fall through to /login
 */
export function BiometricUnlock() {
  const navigate = useNavigate();
  const { setToken, setUser, setBiometricUnlocked } = useAppStore();
  const [biometryLabel, setBiometryLabel] = useState<BiometryLabel>('Biometrics');
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Latched after the first verify failure. Tells the "Use password
  // instead" button to also clear the biometric enrollment, so the
  // post-login flow re-prompts the user to enable biometric. Without
  // this latch, a user whose Keychain entry is invalidated (device
  // biometric re-enrollment, signing-team change, etc.) is stuck:
  // they can sign in with a password but the next launch sends them
  // back to this screen where verify keeps failing forever.
  const [verifyFailed, setVerifyFailed] = useState(false);
  // Prevent the auto-prompt from re-firing if React re-renders the component
  // (StrictMode double-mount in dev, or a navigate-then-back during refresh).
  const promptedRef = useRef(false);

  const fallbackToLogin = useCallback(
    async (clearEnrollment: boolean) => {
      if (clearEnrollment) {
        await disableBiometric();
      }
      setBiometricUnlocked(false);
      navigate('/login', { replace: true });
    },
    [navigate, setBiometricUnlocked],
  );

  const runUnlock = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      await verifyBiometric();
    } catch (e) {
      // Log the underlying plugin error so it shows in Xcode console / Safari
      // Web Inspector. Helps diagnose the difference between a user-cancel
      // (taps Cancel on the system sheet) and a Keychain-policy failure
      // (Keychain entry's biometric ACL was invalidated by a device-level
      // biometric re-enrollment, app reinstall under a different signing
      // team, etc.) — both surface as a thrown error here.
      console.warn('biometric verify failed:', e);
      setVerifyFailed(true);
      setError(
        `${biometryLabel} didn't unlock. Try again, or use your password — ` +
          `signing in with your password will let you re-enable ${biometryLabel}.`,
      );
      setBusy(false);
      return;
    }

    // Biometric passed — exchange the refresh-token cookie for a new JWT.
    try {
      const response = await refreshTokenRequest();
      setToken(response.accessToken);

      // Re-fetch the current user so the store has fresh role/permissions.
      // /auth/refresh returns the access token but not always the full user.
      try {
        const user = await getCurrentUser();
        setUser(user);
      } catch {
        // If we can't load the user, fall through to login.
        await fallbackToLogin(false);
        return;
      }

      setBiometricUnlocked(true);
      // Field workers go straight back to /field; everyone else to /dashboard.
      // The native shell defaults to /field via App.tsx so default here too.
      navigate('/field', { replace: true });
    } catch {
      // Refresh cookie expired or the user was revoked server-side. The user
      // has to fully log in again — clear the biometric enrollment so the
      // next launch doesn't loop them through this screen.
      toast.error('Your session expired. Please sign in again.');
      await fallbackToLogin(true);
    }
  }, [biometryLabel, navigate, setToken, setUser, setBiometricUnlocked, fallbackToLogin]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Guard against routing here when biometric isn't actually enabled or
      // we're not in the native shell — shouldn't happen via App.tsx but
      // protects against deep-linking / refresh.
      if (!isNativeApp() || !isBiometricEnabled()) {
        navigate('/login', { replace: true });
        return;
      }
      const label = await getBiometryLabel();
      if (cancelled) return;
      setBiometryLabel(label);

      if (!promptedRef.current) {
        promptedRef.current = true;
        await runUnlock();
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, runUnlock]);

  const Icon = biometryLabel === 'Face ID' ? ScanFace : Fingerprint;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 px-4 py-12">
      <Card className="w-full max-w-sm shadow-xl" variant="elevated">
        <div className="flex flex-col items-center text-center gap-6 p-2">
          <div className="rounded-full bg-primary/10 p-6">
            <Icon className="w-12 h-12 text-primary" aria-hidden="true" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Welcome back</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Unlock Dispatch with {biometryLabel}.
            </p>
          </div>
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {error}
            </p>
          )}
          <div className="flex flex-col w-full gap-2">
            <Button onClick={runUnlock} disabled={busy} className="w-full">
              {busy ? 'Authenticating…' : `Use ${biometryLabel}`}
            </Button>
            <Button
              variant="ghost"
              // If verify has failed at least once, clearing enrollment on
              // the way to /login flips the post-login flow back into the
              // "offer biometric enrollment" branch. That's the recovery
              // path for an invalidated Keychain entry: a fresh enrollment
              // overwrites the stale entry with a new biometric ACL.
              // When the user is just choosing password by preference on
              // the first try (no failure yet), we leave enrollment intact.
              onClick={() => fallbackToLogin(verifyFailed)}
              disabled={busy}
              className="w-full"
            >
              Use password instead
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

export default BiometricUnlock;
